import React, { useState } from 'react';
import { buildOptionSymbol, emptyOptionParts, parseOptionSymbolParts } from './optionSymbolUtils';

export default function TradeSymbolInput({ symbol, symbols, onChange, disabled, onModeChange }) {
  const [mode, setMode] = useState('symbol');
  const [parts, setParts] = useState(emptyOptionParts);
  const result = buildOptionSymbol(parts);

  const changeMode = (nextMode) => {
    if (disabled || mode === nextMode) return;
    if (nextMode === 'option') {
      const nextParts = parseOptionSymbolParts(symbol) || (!symbol.trim() ? parts : {
        ...emptyOptionParts(),
        underlying: /^[A-Z][A-Z0-9.]{0,5}$/i.test(symbol.trim()) ? symbol.trim().toUpperCase() : '',
      });
      setParts(nextParts);
      onChange(buildOptionSymbol(nextParts).symbol);
    }
    setMode(nextMode);
    onModeChange?.(nextMode);
  };

  const changePart = (field, value) => {
    if (disabled) return;
    const nextParts = { ...parts, [field]: value };
    setParts(nextParts);
    onChange(buildOptionSymbol(nextParts).symbol);
  };

  return (
    <div className="mb-3">
      <div className="btn-group w-100 mb-2" role="group" aria-label="Symbol entry mode">
        {['symbol', 'option'].map((value) => (
          <button
            key={value}
            type="button"
            className={`btn ${mode === value ? 'btn-primary' : 'btn-outline-primary'}`}
            aria-pressed={mode === value}
            disabled={disabled}
            onClick={() => changeMode(value)}
          >
            {value === 'symbol' ? 'Symbol' : 'Option'}
          </button>
        ))}
      </div>
      {mode === 'symbol' ? (
        <>
          <label htmlFor="trade-symbol" className="form-label fw-semibold">Symbol</label>
          <input
            id="trade-symbol"
            type="text"
            className="form-control"
            list="trade-symbol-suggestions"
            value={symbol}
            onChange={(e) => onChange(e.target.value)}
            placeholder="AAPL"
            disabled={disabled}
            required
          />
          <datalist id="trade-symbol-suggestions">
            {symbols.map((value) => <option key={value} value={value} />)}
          </datalist>
          <div className="form-text">Pick a prior symbol, type a symbol, or use Option to build a contract symbol.</div>
        </>
      ) : (
        <>
          <div className="row g-2">
            <div className="col-6 col-md-2">
              <label htmlFor="option-underlying" className="form-label fw-semibold text-nowrap">Symbol</label>
              <input
                id="option-underlying"
                className="form-control"
                value={parts.underlying}
                onChange={(e) => changePart('underlying', e.target.value.toUpperCase())}
                placeholder="RKLB"
                maxLength={6}
                disabled={disabled}
                required
              />
            </div>
            {[
              { field: 'year', label: 'Ex. Year', min: 2000, max: 2099, placeholder: '2026' },
              { field: 'month', label: 'Ex. Month', min: 1, max: 12, placeholder: '10' },
              { field: 'day', label: 'Ex. Day', min: 1, max: 31, placeholder: '2' },
            ].map(({ field, label, min, max, placeholder }) => (
              <div className="col-6 col-md-2" key={field}>
                <label htmlFor={`option-${field}`} className="form-label fw-semibold text-nowrap">{label}</label>
                <input
                  id={`option-${field}`}
                  type="number"
                  className="form-control"
                  value={parts[field]}
                  onChange={(e) => changePart(field, e.target.value)}
                  min={min}
                  max={max}
                  step="1"
                  placeholder={placeholder}
                  disabled={disabled}
                  required
                />
              </div>
            ))}
            <div className="col-6 col-md-2">
              <label htmlFor="option-type" className="form-label fw-semibold text-nowrap">Call / Put</label>
              <select
                id="option-type"
                className="form-select"
                value={parts.optionType}
                onChange={(e) => changePart('optionType', e.target.value)}
                disabled={disabled}
              >
                <option value="C">Call (C)</option>
                <option value="P">Put (P)</option>
              </select>
            </div>
            <div className="col-6 col-md-2">
              <label htmlFor="option-strike" className="form-label fw-semibold text-nowrap">Strike Price</label>
              <input
                id="option-strike"
                type="number"
                className="form-control"
                min="0.001"
                max="99999.999"
                step="0.001"
                value={parts.strike}
                onChange={(e) => changePart('strike', e.target.value)}
                placeholder="90"
                disabled={disabled}
                required
              />
            </div>
          </div>
          {result.symbol ? (
            <div className="form-text">
              Contract Symbol: <output aria-label="Contract Symbol">{result.symbol}</output>
            </div>
          ) : (
            <div className="form-text text-danger" role="alert">{result.error}</div>
          )}
          <div className="form-text">The strike price is the contract strike, not the trade's execution price.</div>
        </>
      )}
    </div>
  );
}
